import { type ThemeOptions } from '@mui/material';

import LogoCollapsed from '../static/images/ayrade-brand-mark.svg';
import LogoText from '../static/images/ayrade-wordmark-dark.svg';
import { hexToRGB } from '../utils/Colors';
import { fileUri } from '../utils/Environment';
import applianceComponents from './ApplianceDesign';
import { FONT_FAMILY_CODE, type LabelColor, LabelColorDict } from './Theme';

const EE_COLOR = '#5fd4c2';

export const THEME_DARK_DEFAULT_BACKGROUND = '#0d121b';
const THEME_DARK_DEFAULT_PRIMARY = '#3399FF';
const THEME_DARK_DEFAULT_SECONDARY = '#66B2FF';
const THEME_DARK_DEFAULT_ACCENT = '#071B33';
const THEME_DARK_DEFAULT_PAPER = '#151c28';
const THEME_DARK_DEFAULT_NAV = '#151c28';

const ThemeDark = (
  logo: string | null = null,
  logo_collapsed: string | null = null,
  background: string | null = null,
  paper: string | null = null,
  nav: string | null = null,
  primary: string | null = null,
  secondary: string | null = null,
  accent: string | null = null,
  text_color = '#ffffff',
): ThemeOptions => ({
  logo: logo || fileUri(LogoText),
  logo_collapsed: logo_collapsed || fileUri(LogoCollapsed),
  borderRadius: 16,
  shape: { borderRadius: 16 },
  palette: {
    mode: 'dark',
    common: {
      white: '#ffffff',
      black: '#000000',
      grey: '#98a2b3',
      lightGrey: 'rgba(234, 240, 255, 0.72)',
    },
    text: {
      primary: text_color,
      secondary: 'rgba(234, 240, 255, 0.68)',
      disabled: 'rgba(234, 240, 255, 0.38)',
    },
    divider: 'rgba(234, 240, 255, 0.1)',
    action: {
      hover: hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.12),
      selected: hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.18),
      disabledBackground: 'rgba(234, 240, 255, 0.06)',
    },
    error: {
      main: '#f44336',
      dark: '#c62828',
    },
    warn: { main: '#ffa726' },
    dangerZone: {
      main: '#f6685e',
      light: '#fbc2be',
      dark: '#f44336',
      contrastText: '#000000',
    },
    success: { main: '#34c759' },
    warning: { main: '#ffa726' },
    primary: { main: primary || THEME_DARK_DEFAULT_PRIMARY },
    secondary: { main: secondary || THEME_DARK_DEFAULT_SECONDARY },
    gradient: { main: '#7ab8ff' },
    border: {
      primary: hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.3),
      secondary: hexToRGB(secondary || THEME_DARK_DEFAULT_SECONDARY, 0.3),
      pagination: hexToRGB('#d8e6ff', 0.42),
      paper: hexToRGB('#d8e6ff', 0.12),
    },
    pagination: { main: '#e8f0ff' },
    chip: { main: '#e8f0ff' },
    labelChipMap: new Map<string, LabelColor>([
      [
        LabelColorDict.Red, {
          backgroundColor: 'rgba(255, 90, 95, 0.12)',
          color: '#ff6b6f',
        }], [
        LabelColorDict.Green, {
          backgroundColor: 'rgba(52, 199, 89, 0.14)',
          color: '#65db84',
        }], [
        LabelColorDict.Orange, {
          backgroundColor: 'rgba(255, 179, 64, 0.12)',
          color: '#ffb95a',
        }],
    ]),
    ai: {
      main: '#9ab7ff',
      light: '#c7d8ff',
      dark: '#5e87ff',
      contrastText: '#07111f',
    },
    ee: {
      main: EE_COLOR,
      contrastText: '#ffffff',
      background: hexToRGB(EE_COLOR, 0.2),
      lightBackground: hexToRGB(EE_COLOR, 0.08),
    },
    xtmhub: { main: '#72beff' },
    background: {
      default: background || THEME_DARK_DEFAULT_BACKGROUND,
      paper: paper || THEME_DARK_DEFAULT_PAPER,
      nav: nav || THEME_DARK_DEFAULT_NAV,
      accent: accent || THEME_DARK_DEFAULT_ACCENT,
      shadow: 'rgba(0, 0, 0, 0.28)',
      code: accent || THEME_DARK_DEFAULT_ACCENT,
      paperInCard: '#111827',
    },
    widgets: {
      securityDomains: {
        colors: {
          success: 'rgb(2,129,8)',
          intermediate: 'rgb(255 216 0)',
          warning: 'rgb(245, 166, 35)',
          failed: 'rgb(220, 81, 72)',
          pending: 'rgba(248,243,243,0.37)',
          unknown: 'rgba(73,72,72,0.37)',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    body2: {
      fontSize: '0.8rem',
      lineHeight: '1.2rem',
      color: text_color,
    },
    body1: {
      fontSize: '0.9rem',
      color: text_color,
    },
    overline: {
      fontWeight: 500,
      color: text_color,
    },
    h1: {
      margin: '0 0 10px 0',
      padding: 0,
      fontWeight: 600,
      fontSize: 24,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
      color: text_color,
    },
    h2: {
      margin: '0 0 10px 0',
      padding: 0,
      fontWeight: 600,
      fontSize: 16,
      textTransform: 'none',
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
      color: text_color,
    },
    h3: {
      margin: '0 0 10px 0',
      padding: 0,
      fontWeight: 400,
      fontSize: 13,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
      color: text_color,
    },
    h4: {
      height: 15,
      margin: '0 0 10px 0',
      padding: 0,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      color: text_color,
    },
    h5: {
      fontWeight: 400,
      fontSize: 13,
      textTransform: 'none',
      marginTop: -4,
      color: text_color,
    },
    h6: {
      fontWeight: 400,
      fontSize: 18,
      color: text_color,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 18,
      color: text_color,
    },
  },
  components: {
    MuiFormControl: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiTextField: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiSelect: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(234, 240, 255, 0.08)',
          boxShadow: '0 12px 34px rgba(0, 0, 0, 0.24)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollbarColor: `${accent || THEME_DARK_DEFAULT_ACCENT} ${nav || THEME_DARK_DEFAULT_NAV}`,
          scrollbarWidth: 'thin',
        },
        body: {
          'scrollbarColor': `${accent || THEME_DARK_DEFAULT_ACCENT} ${nav || THEME_DARK_DEFAULT_NAV}`,
          'scrollbarWidth': 'thin',
          'background':
            `radial-gradient(ellipse at 10% 0%, ${hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.10)}, transparent 40%), radial-gradient(ellipse at 90% 20%, ${hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.06)}, transparent 40%), ${background || THEME_DARK_DEFAULT_BACKGROUND}`,
          'html': { WebkitFontSmoothing: 'auto' },
          'a': { color: primary || THEME_DARK_DEFAULT_PRIMARY },
          'input:-webkit-autofill': {
            WebkitAnimation: 'autofill 0s forwards',
            animation: 'autofill 0s forwards',
            WebkitTextFillColor: '#ffffff !important',
            caretColor: 'transparent !important',
            WebkitBoxShadow:
              '0 0 0 1000px rgba(11, 23, 40, 1) inset !important',
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          },
          'pre': {
            fontFamily: FONT_FAMILY_CODE,
            color: `${text_color} !important`,
            background: `${accent || THEME_DARK_DEFAULT_ACCENT} !important`,
            borderRadius: 4,
          },
          'pre.light': {
            fontFamily: FONT_FAMILY_CODE,
            background: `${nav || THEME_DARK_DEFAULT_NAV} !important`,
            borderRadius: 4,
          },
          'code': {
            fontFamily: FONT_FAMILY_CODE,
            color: `${text_color} !important`,
            background: `${accent || THEME_DARK_DEFAULT_ACCENT} !important`,
            padding: 3,
            fontSize: 12,
            fontWeight: 400,
            borderRadius: 4,
          },
          '.w-md-editor': {
            'boxShadow': 'none',
            'background': 'transparent',
            'borderBottom': '1px solid rgba(216, 230, 255, 0.38) !important',
            'transition': 'borderBottom .3s',
            '&:hover': { borderBottom: '2px solid rgba(216, 230, 255, 0.7) !important' },
            '&:focus-within': { borderBottom: `2px solid ${primary || THEME_DARK_DEFAULT_PRIMARY} !important` },
          },
          '.error .w-md-editor': {
            'border': '0 !important',
            'borderBottom': '2px solid #f44336 !important',
            '&:hover': {
              border: '0 !important',
              borderBottom: '2px solid #f44336 !important',
            },
            '&:focus': {
              border: '0 !important',
              borderBottom: '2px solid #f44336 !important',
            },
          },
          '.w-md-editor-toolbar': {
            border: '0 !important',
            backgroundColor: 'transparent !important',
            color: `${text_color} !important`,
          },
          '.w-md-editor-toolbar li button': { color: `${text_color} !important` },
          '.w-md-editor-text textarea': {
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: 13,
            color: text_color,
          },
          '.w-md-editor-preview': { boxShadow: 'inset 1px 0 0 0 rgba(216, 230, 255, 0.28)' },
          '.wmde-markdown': {
            background: 'transparent',
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: 13,
            color: text_color,
          },
          '.wmde-markdown tr': { background: 'transparent !important' },
          '.react-grid-placeholder': { backgroundColor: `${accent || THEME_DARK_DEFAULT_ACCENT} !important` },
          '.react_time_range__track': {
            backgroundColor: hexToRGB(primary || THEME_DARK_DEFAULT_PRIMARY, 0.16) + ' !important',
            borderLeft: `1px solid ${primary || THEME_DARK_DEFAULT_PRIMARY} !important`,
            borderRight: `1px solid ${primary || THEME_DARK_DEFAULT_PRIMARY} !important`,
          },
          '.react_time_range__handle_marker': { backgroundColor: `${primary || THEME_DARK_DEFAULT_PRIMARY} !important` },
          '.leaflet-container': { backgroundColor: `${paper || THEME_DARK_DEFAULT_PAPER} !important` },
          '.react-grid-item .react-resizable-handle::after': {
            borderRight: '2px solid rgba(216, 230, 255, 0.32) !important',
            borderBottom: '2px solid rgba(216, 230, 255, 0.32) !important',
          },
        },
      },
    },
    MuiInputBase: { styleOverrides: { root: { color: text_color } } },
    ...applianceComponents('dark', primary || THEME_DARK_DEFAULT_PRIMARY, paper || THEME_DARK_DEFAULT_PAPER),
  },
});

export default ThemeDark;
